import { Component } from '@theme/component';

class MartGuardServiceTabs extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  disconnectedCallback() {
    this.controller?.abort();
    super.disconnectedCallback();
  }

  initialize() {
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.tabs = [...this.querySelectorAll('[data-service-tab]')];
    this.panels = [...this.querySelectorAll('[data-service-panel]')];
    const list = this.querySelector('[data-service-tablist]');
    if (!list || !this.tabs.length || !this.panels.length) return;
    list.setAttribute('role', 'tablist');
    for (const tab of this.tabs) {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', `service-${tab.dataset.serviceTab}`);
    }
    for (const panel of this.panels) panel.setAttribute('role', 'tabpanel');
    this.search = this.querySelector('[data-service-search]');
    this.questions = [...this.querySelectorAll('.mg-service-question')];
    this.topics = [...this.querySelectorAll('.mg-service-topic')];
    this.status = this.querySelector('[data-service-status]');
    this.empty = this.querySelector('[data-service-empty]');
    this.clearButton = this.querySelector('[data-service-clear]');
    const searchWrap = this.querySelector('[data-service-search-wrap]');
    if (searchWrap) searchWrap.hidden = false;
    this.search?.addEventListener('input', () => this.filterQuestions(), { signal });
    this.clearButton?.addEventListener('click', () => {
      this.resetSearch();
      this.select(this.activeCategory);
      this.search?.focus();
    }, { signal });
    this.search?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && this.search.value.trim()) {
        event.preventDefault();
        const first = this.questions.find(question => !question.hidden);
        (first || this.empty)?.scrollIntoView({ block: 'center' });
        first?.querySelector('summary')?.focus({ preventScroll: true });
        return;
      }
      if (event.key !== 'Escape') return;
      this.resetSearch();
      this.select(this.activeCategory);
    }, { signal });
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const shortcut = event.target.closest('[data-service-shortcut]');
      if (shortcut && this.contains(shortcut)) {
        event.preventDefault();
        this.resetSearch();
        this.select(shortcut.dataset.serviceShortcut);
        history.replaceState(null, '', `#service-${shortcut.dataset.serviceShortcut}`);
        this.querySelector('[data-service-tablist]')?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
        this.tabs.find(tab => tab.dataset.serviceTab === shortcut.dataset.serviceShortcut)?.focus({ preventScroll: true });
        return;
      }
      const tab = event.target.closest('[data-service-tab]');
      if (!this.tabs.includes(tab)) return;
      event.preventDefault();
      this.resetSearch();
      this.select(tab.dataset.serviceTab);
      history.replaceState(null, '', `#service-${tab.dataset.serviceTab}`);
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (!(event.target instanceof Element)) return;
      const tab = event.target.closest('[data-service-tab]');
      const index = this.tabs.indexOf(tab);
      if (index < 0) return;
      let next;
      if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;
      if (next === undefined) return;
      event.preventDefault();
      this.resetSearch();
      this.tabs[next].focus({ preventScroll: true });
      this.select(this.tabs[next].dataset.serviceTab);
      history.replaceState(null, '', `#service-${this.tabs[next].dataset.serviceTab}`);
    }, { signal });
    window.addEventListener('hashchange', () => this.selectFromLocation(), { signal });
    this.selectFromLocation();
  }

  selectFromLocation() {
    this.resetSearch();
    let id;
    try { id = decodeURIComponent(location.hash.slice(1)); } catch { id = ''; }
    const target = id ? document.getElementById(id) : null;
    const panel = target?.closest('[data-service-panel]');
    const category = this.panels.includes(panel) ? panel.dataset.servicePanel : this.dataset.defaultCategory;
    this.select(category);
    let ancestor = target;
    while (ancestor) {
      if (ancestor instanceof HTMLDetailsElement) ancestor.open = true;
      ancestor = ancestor.parentElement;
    }
  }

  select(category) {
    if (!this.tabs.some(tab => tab.dataset.serviceTab === category)) category = this.tabs[0]?.dataset.serviceTab;
    this.activeCategory = category;
    for (const tab of this.tabs) {
      const selected = tab.dataset.serviceTab === category;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    }
    for (const panel of this.panels) panel.hidden = panel.dataset.servicePanel !== category;
  }

  resetSearch() {
    if (this.search) this.search.value = '';
    this.classList.remove('mg-care-searching');
    if (this.status) { this.status.hidden = true; this.status.textContent = ''; }
    if (this.empty) this.empty.hidden = true;
    if (this.clearButton) this.clearButton.hidden = true;
    for (const question of this.questions) question.hidden = false;
    for (const topic of this.topics) topic.hidden = false;
    for (const panel of this.panels) panel.setAttribute('role', 'tabpanel');
    this.querySelector('.mg-service-tabs__panels').hidden = false;
  }

  filterQuestions() {
    const normalize = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
    const words = normalize(this.search.value.trim()).split(/\s+/).filter(Boolean);
    if (!words.length) {
      this.resetSearch();
      this.select(this.activeCategory);
      return;
    }
    this.classList.add('mg-care-searching');
    this.clearButton.hidden = false;
    let count = 0;
    for (const question of this.questions) {
      const text = normalize(question.textContent);
      question.hidden = !words.every(word => text.includes(word));
      if (!question.hidden) count++;
    }
    for (const topic of this.topics) topic.hidden = ![...topic.querySelectorAll('.mg-service-question')].some(question => !question.hidden);
    for (const panel of this.panels) {
      panel.hidden = ![...panel.querySelectorAll('.mg-service-question')].some(question => !question.hidden);
      panel.setAttribute('role', 'region');
    }
    for (const tab of this.tabs) tab.setAttribute('aria-selected', 'false');
    this.status.hidden = false;
    this.status.textContent = count === 1 ? this.dataset.resultOne : this.dataset.resultsLabel.replace('{count}', String(count));
    this.empty.hidden = count !== 0;
    this.querySelector('.mg-service-tabs__panels').hidden = count === 0;
  }
}

if (!customElements.get('mg-service-tabs')) customElements.define('mg-service-tabs', MartGuardServiceTabs);
